import { welfordSummary } from '/bb/aggregators/welford.js';
import { trajectorySummary } from '/bb/aggregators/trajectory.js';
import { getAttributesMode } from '/bb/attributes-config.js';
import { clampUnitInterval } from '/bb/normalizers.js';

/**
 * Convert the in-memory state to the wire schema (`metrics`).
 * Empty branches are elided so the payload stays compact.
 */
export function serializeMetrics(s, nowMs) {
  const ended = nowMs;
  const wallMs = ended - (s.startedAt || ended);
  const activeMs = Math.max(0, wallMs - (s.pausedTotalMs || 0));

  const pages = {};
  for (const [pageKey, agg] of s.pages.entries()) {
    pages[pageKey] = serializePageEntry(s, pageKey, agg, ended);
  }

  return omitEmpty({
    session: {
      started_at: s.startedAt || 0,
      attributes_mode: getAttributesMode(),
      ended_at: ended,
      duration_ms: wallMs,
      active_duration_ms: activeMs,
      sessions_lifetime: s.session.priorSessionCount,
      active_days_lifetime: s.session.activeDaysCount,
      page_focus_count: s.session.pageFocusCount,
      orientation_change_count: s.session.orientationChangeCount,
      pause_before_submit_ms: s.session.pauseBeforeSubmitMs,
      pointer: serializePointerBucket(s.session.pointer, { includeLatency: true }),
      touch: serializePointerBucket(s.session.touch, { includeLatency: false }),
    },
    pages,
  });
}

function serializePointerBucket(p, opts) {
  if (!p || p.sampleCount === 0) {
    if (p && (p.clicks.total > 0 || p.rightClicks > 0 || p.scroll.count > 0)) {
      // fallthrough — pointer had non-motion activity
    } else {
      return null;
    }
  }
  const speed = welfordSummary(p.speed);
  const accel = welfordSummary(p.accel);
  const latency = welfordSummary(p.latency);
  const accPx = welfordSummary(p.accuracyPx);
  const accNorm = welfordSummary(p.accuracyNorm);
  const pathDeviation = welfordSummary(p.pathDeviation);
  const pressure = welfordSummary(p.pressure);
  const width = welfordSummary(p.width);
  const scrollSpeed = welfordSummary(p.scroll.speed);
  const trajectories = trajectorySummary(p.trajectory);
  const out = {
    avg_speed: round(speed.avg, 4),
    max_speed: round(speed.max, 4),
    avg_acceleration: round(accel.avg, 4),
    max_acceleration: round(accel.max, 4),
    distance_total: round(p.sumDist, 2),
    time_total: round(p.sumTime, 1),
    sample_count: p.sampleCount,
    right_click_total: p.rightClicks,
    clicks: { total: p.clicks.total, on_interactive: p.clicks.onInteractive },
    accuracy: {
      avg_px: round(accPx.avg, 2),
      max_px: round(accPx.max, 2),
      avg_norm: round(accNorm.avg, 4),
      sample_count: accPx.count,
    },
    scroll: {
      count: p.scroll.count,
      avg_speed: round(scrollSpeed.avg, 4),
      max_speed: round(scrollSpeed.max, 4),
      depth_max: round(clampUnitInterval(p.scroll.depthMax), 4),
    },
    trajectories,
    direction_changes: trajectories.circling + trajectories.backtracking,
    path_deviation: {
      avg_px: round(pathDeviation.avg, 2),
      max_px: round(pathDeviation.max, 2),
      sample_count: pathDeviation.count,
    },
  };
  if (pressure.count > 0) {
    out.pressure = {
      avg: round(pressure.avg, 4),
      var: round(pressure.var, 6),
      sample_count: pressure.count,
    };
  }
  if (width.count > 0) {
    out.width = {
      avg: round(width.avg, 4),
      var: round(width.var, 6),
      sample_count: width.count,
    };
  }
  if (opts && opts.includeLatency) {
    out.latency = { avg_ms: round(latency.avg, 2), sample_count: latency.count };
  }
  return out;
}

function serializePageEntry(s, pageKey, agg, nowMs) {
  let durationMs = agg.duration_ms;
  if (s.currentPageKey === pageKey && s.currentPageEnteredAt > 0) {
    durationMs += Math.max(0, nowMs - s.currentPageEnteredAt);
  }
  const forms = {};
  for (const [key, formAgg] of s.forms.entries()) {
    if (formAgg.pageKey !== pageKey) continue;
    forms[formAgg.formId] = serializeFormEntry(s, formAgg, nowMs);
  }
  const sections = {};
  for (const [key, secAgg] of s.sections.entries()) {
    if (secAgg.pageKey !== pageKey) continue;
    sections[secAgg.sectionId] = serializeSectionEntry(secAgg, nowMs);
  }
  return omitEmpty({
    duration_ms: round(durationMs, 1),
    visit_count: agg.visit_count,
    visit_count_lifetime: agg.visit_count_lifetime,
    forms,
    sections,
  });
}

function serializeFormEntry(s, f, nowMs) {
  let timeOnFormMs = f.time_on_form_ms;
  let activeTimeOnFormMs = f.active_time_on_form_ms;
  if (f.runtime.currentlyFocused && f.runtime.activeSince > 0) {
    activeTimeOnFormMs += Math.max(0, nowMs - f.runtime.activeSince);
    timeOnFormMs = Math.max(timeOnFormMs, nowMs - f.runtime.firstFocusAt);
  } else if (f.runtime.firstFocusAt > 0) {
    timeOnFormMs = Math.max(timeOnFormMs, f.runtime.lastFocusOutAt - f.runtime.firstFocusAt);
  }
  const fields = {};
  for (const [k, fld] of s.fields.entries()) {
    if (!k.startsWith(f.pageKey + '::' + f.formId + '::')) continue;
    fields[fld.name] = serializeFieldEntry(fld, nowMs);
  }
  return omitEmpty({
    is_submitted: !!f.is_submitted,
    submission_time_ms: f.submission_time_ms,
    time_on_form_ms: round(timeOnFormMs, 1),
    active_time_on_form_ms: round(activeTimeOnFormMs, 1),
    visits_before_submission_count: f.visits_before_submission_count,
    visit_count_session: f.visit_count_session,
    visit_count_lifetime: f.visit_count_lifetime,
    corrections: serializeFormCorrections(f),
    fields,
  });
}

function serializeFormCorrections(f) {
  if (!f.corrections || f.corrections.total_count <= 0) return null;

  return {
    early_count: f.corrections.early_count,
    late_count: f.corrections.late_count,
    total_count: f.corrections.total_count,
    late_ratio: round(f.corrections.late_ratio, 4),
  };
}

function serializeFieldEntry(fld, nowMs) {
  const isText = fld.keystroke != null;
  if (!isText) {
    if (fld.type === 'file') {
      const selectionLatency = welfordSummary(fld._selection_latency);
      return omitEmpty({
        name: fld.name,
        type: fld.type,
        field_categories: fld.field_categories.slice(),
        changes_count: fld.changes_count || 0,
        files_count_last: fld.files_count_last || 0,
        files_count_total: fld.files_count_total || 0,
        files_count_max: fld.files_count_max || 0,
        multiple_enabled: !!fld.multiple_enabled,
        multiple_selection_count: fld.multiple_selection_count || 0,
        cancel_count: fld.cancel_count || 0,
        selection_latency_avg_ms: round(selectionLatency.avg, 2),
        selection_latency_max_ms: round(selectionLatency.max, 2),
        selection_latency_count: selectionLatency.count,
      });
    }
    if (fld.type === 'range') {
      const focusDurationMs =
        fld.runtime.currentlyFocused && fld.runtime.lastFocusInAt > 0
          ? fld.focus_duration_ms + Math.max(0, nowMs - fld.runtime.lastFocusInAt)
          : fld.focus_duration_ms;
      return omitEmpty({
        name: fld.name,
        type: fld.type,
        field_categories: fld.field_categories.slice(),
        focus_duration_ms: round(focusDurationMs, 1),
        changes_count: fld.changes_count || 0,
        value_to_max_ratio: fld.value_to_max_ratio,
        value_looks_rounded: fld.value_looks_rounded,
        max_amt_flag: fld.max_amt_flag,
      });
    }
    return omitEmpty({
      name: fld.name,
      type: fld.type,
      field_categories: fld.field_categories.slice(),
      changes_count: fld.changes_count || 0,
    });
  }
  const dwell = welfordSummary(fld.keystroke.dwell);
  const flight = welfordSummary(fld.keystroke.flight);
  const correctionLatency = welfordSummary(fld.corrections._latency);
  const focusDurationMs =
    fld.runtime.currentlyFocused && fld.runtime.lastFocusInAt > 0
      ? fld.focus_duration_ms + Math.max(0, nowMs - fld.runtime.lastFocusInAt)
      : fld.focus_duration_ms;

  return omitEmpty({
    name: fld.name,
    type: fld.type,
    field_categories: fld.field_categories.slice(),
    pause_before_focus_ms: fld.pause_before_focus_ms,
    focus_duration_ms: round(focusDurationMs, 1),
    focus_by_click_count: fld.focus_by_click_count,
    focus_by_tab_count: fld.focus_by_tab_count,
    empty_focus_count: fld.empty_focus_count,
    pause_before_input_ms: fld.pause_before_input_ms,
    value_length: fld.value_length,
    revisit_count: fld.revisit_count,
    revision_count: fld.revision_count,
    reenter_count: fld.reenter_count,
    reenter_by_paste_count: fld.reenter_by_paste_count,
    abandonment_and_return_count: fld.abandonment_and_return_count,
    returned_to_original_count: fld.returned_to_original_count,
    completion_time_ms: fld.completion_time_ms,
    value_looks_rounded: fld.value_looks_rounded,
    corrections: {
      count: fld.corrections.count,
      depth_avg: round(welfordSummary(fld.corrections._bursts).avg, 3),
      depth_max: round(welfordSummary(fld.corrections._bursts).max, 3),
      latency_avg_ms: round(correctionLatency.avg, 2),
      latency_max_ms: round(correctionLatency.max, 2),
      latency_count: correctionLatency.count,
      backspace_count: fld.corrections.backspace_count,
      del_count: fld.corrections.del_count,
      replace_count: fld.corrections.replace_count,
    },
    paste_count: fld.paste_count,
    edit_after_paste_count: fld.edit_after_paste_count,
    right_click_count: fld.right_click_count,
    autocomplete: {
      detected: fld.autocomplete.detected,
      edits_after: fld.autocomplete.edits_after,
      accepted_count: fld.autocomplete_accepted_count,
    },
    keystroke: {
      total_count: fld.keystroke.total_count,
      dwell_time_avg_ms: round(dwell.avg, 2),
      flight_time_avg_ms: round(flight.avg, 2),
      typing_speed_keys_s: round(typingSpeedKeysPerSecond(fld), 4),
      typing_rhythm: {
        dwell_var_ms2: round(welfordSummary(fld.keystroke.dwell).var, 2),
        flight_var_ms2: round(flight.var, 2),
      },
      overlap_count: fld.keystroke.overlap_count,
      long_pause_count: fld.keystroke.long_pause_count,
    },
  });
}

function typingSpeedKeysPerSecond(fld) {
  const totalKeyCount = fld.keystroke.total_count;
  const firstTypingAt = fld.keystroke.first_typing_at;
  const lastTypingAt = fld.keystroke.last_typing_at;

  if (totalKeyCount <= 1 || firstTypingAt <= 0) return 0;

  const durationSeconds = (lastTypingAt - firstTypingAt) / 1000;

  if (durationSeconds <= 0) return 0;

  return totalKeyCount / durationSeconds;
}

function serializeSectionEntry(sec, nowMs) {
  let timeTotal = sec.time_total_ms;
  let readingMs = sec.reading_time_ms;
  if (sec.runtime.currentlyVisible && sec.runtime.enteredAt > 0) {
    timeTotal += Math.max(0, nowMs - sec.runtime.enteredAt);
    if (sec.runtime.lastActivityAt > 0) {
      readingMs += Math.max(0, sec.runtime.lastActivityAt - sec.runtime.enteredAt);
    }
  }
  const scrollSpeed = welfordSummary(sec.scroll.speed);
  return omitEmpty({
    text_length: sec.text_length,
    visits_count: sec.visits_count,
    section_categories: sec.section_categories.slice(),
    visits: sec.visits.slice(),
    reading_time_ms: round(readingMs, 1),
    pause_count: sec.pause_count,
    time_total_ms: round(timeTotal, 1),
    scroll: {
      count: sec.scroll.count,
      avg_speed: round(scrollSpeed.avg, 4),
      max_speed: round(scrollSpeed.max, 4),
      depth_max: round(clampUnitInterval(sec.scroll.depthMax), 4),
    },
    zoom_count: sec.zoom_count,
    selection_count: sec.selection_count,
  });
}

function round(n, decimals) {
  if (!Number.isFinite(n)) return 0;
  const p = Math.pow(10, decimals);
  return Math.round(n * p) / p;
}

function omitEmpty(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = Array.isArray(obj) ? [] : {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v == null) {
      if (v === null) out[k] = null;
      continue;
    }
    if (typeof v === 'object' && !Array.isArray(v)) {
      const inner = omitEmpty(v);
      if (inner && Object.keys(inner).length === 0) continue;
      out[k] = inner;
    } else if (Array.isArray(v)) {
      out[k] = v.slice();
    } else {
      out[k] = v;
    }
  }
  return out;
}
